import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import ApprenantPageShell from './ApprenantPageShell';
import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import { prepareFormationSubscriptionPayment } from '../../utils/formationSubscriptionFlow';
import { clearPendingFiliereSubscription } from '../../utils/pendingFiliereSubscription';

export default function ApprenantSubscribeFilierePanel() {
  const { filiereId } = useParams<{ filiereId: string }>();
  const navigate = useNavigate();
  const started = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  useEffect(() => {
    if (!filiereId || started.current) return;
    started.current = true;

    void (async () => {
      const result = await prepareFormationSubscriptionPayment(filiereId);
      if (result.ok === false) {
        setError(result.message);
        setErrorCode(result.code);
        return;
      }

      clearPendingFiliereSubscription();
      navigate(`/app/inscription/${result.inscriptionId}/paiement`, {
        replace: true,
        state: {
          montant: result.montantInscription,
          maxAmount: result.maxAmount,
          focusInscription: result.focusInscription,
          allowCustomAmount: result.allowCustomAmount,
          paymentChoice: result.paymentChoice,
          filiereLabel: result.filiereLabel ?? '',
        },
      });
    })();
  }, [filiereId, navigate]);

  return (
    <ApprenantPageShell
      title="Souscription"
      description="Préparation de votre inscription et des frais à régler"
      sideCard={{
        label: 'Étape',
        value: 'Paiement',
        helper: 'Frais d’inscription de la formation',
      }}
      isLoading={!error}
      loadingMessage="Calcul des frais d'inscription selon votre profil…"
    >
      {error ? (
        <div className="mx-auto max-w-lg rounded-2xl border border-outline-variant/20 bg-white p-8 text-center shadow-sm">
          <LucideSymbolIcon
            name={
              errorCode === 'DOSSIER' ? 'folder_open' : errorCode === 'NO_COHORTE' ? 'event_busy' : 'info'
            }
            tone="primary"
            size={40}
            className="mx-auto"
          />
          <h2 className="mt-4 text-lg font-bold text-slate-900">Souscription interrompue</h2>
          <p className="mt-2 text-sm leading-relaxed text-on-surface-variant">{error}</p>
          <div className="mt-6 flex flex-col gap-2">
            {errorCode === 'DOSSIER' ? (
              <Link
                to="/app/documents"
                className="inline-flex items-center justify-center rounded-xl bg-primary px-5 py-3 text-sm font-bold text-white"
              >
                Compléter mon dossier
              </Link>
            ) : null}
            {errorCode === 'ALREADY_PAID' ? (
              <Link
                to="/app/mes-cours"
                className="inline-flex items-center justify-center rounded-xl bg-primary px-5 py-3 text-sm font-bold text-white"
              >
                Voir mes cours
              </Link>
            ) : null}
            {filiereId ? (
              <Link
                to={`/app/formations?filiere=${encodeURIComponent(filiereId)}`}
                className="inline-flex items-center justify-center rounded-xl border border-outline-variant/30 px-5 py-3 text-sm font-bold text-slate-700"
              >
                Retour aux formations
              </Link>
            ) : (
              <Link
                to="/app/formations"
                className="inline-flex items-center justify-center rounded-xl border border-outline-variant/30 px-5 py-3 text-sm font-bold text-slate-700"
              >
                Catalogue formations
              </Link>
            )}
          </div>
        </div>
      ) : null}
    </ApprenantPageShell>
  );
}
